// Small DOM helpers. Everything from the API is untrusted, so this module only
// ever creates nodes and sets text/attributes: no HTML parsing anywhere.

import { fmtDateTime, fmtRelative, fmtUtc, parseDate } from './format';

export type Child = Node | string | number | null | undefined | false;

type AttrValue = string | number | boolean | null | undefined;

export interface Props {
  class?: string;
  text?: string;
  attrs?: Record<string, AttrValue>;
  on?: { [K in keyof HTMLElementEventMap]?: (ev: HTMLElementEventMap[K]) => void };
}

const URL_ATTRS = new Set(['href', 'src', 'action', 'formaction', 'poster', 'xlink:href']);

/**
 * Same-origin path, query or fragment only: never another origin or a javascript: URL.
 * Whitespace and control characters are refused too: browsers strip tabs and newlines
 * from URLs, so "/\t/evil.example" would otherwise become the off-site "//evil.example".
 */
export function isSafePath(value: string): boolean {
  if (/[\u0000- \u007f]/.test(value)) return false;
  return /^\/(?![/\\])/.test(value) || value.startsWith('?') || value.startsWith('#');
}

export function setAttr(el: Element, name: string, value: AttrValue): void {
  if (value === null || value === undefined || value === false) {
    el.removeAttribute(name);
    return;
  }
  const lower = name.toLowerCase();
  if (lower.startsWith('on')) {
    console.warn(`dom: refusing to set ${name}; use addEventListener`);
    return;
  }
  const str = value === true ? '' : String(value);
  if (URL_ATTRS.has(lower) && !isSafePath(str)) {
    console.warn(`dom: refusing non-local ${name}`);
    return;
  }
  el.setAttribute(name, str);
}

export function append(parent: ParentNode, ...children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    // ParentNode.append turns strings into Text nodes.
    parent.append(typeof c === 'number' ? String(c) : c);
  }
}

/** document.createElement with props; string children become text nodes. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Props | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props) {
    if (props.class) el.className = props.class;
    if (props.attrs) for (const [name, value] of Object.entries(props.attrs)) setAttr(el, name, value);
    if (props.on) {
      for (const [type, fn] of Object.entries(props.on)) if (fn) el.addEventListener(type, fn as EventListener);
    }
    if (props.text !== undefined) el.textContent = props.text;
  }
  append(el, ...children);
  return el;
}

export function byId<T extends HTMLElement = HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/** Looks up a required element of the static page; throws a readable error if the HTML is out of date. */
export function requireEl<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`This page is missing a part (#${id}). Try reloading.`);
  return el as T;
}

export function link(href: string, text: string, cls?: string): HTMLAnchorElement {
  return h('a', { class: cls, attrs: { href } }, text);
}

/** A <dt>/<dd> pair wrapped in a <div> (valid inside <dl>), so pairs can be laid out as grid cells. */
export function fact(label: string, value: Child): HTMLElement {
  return h('div', null, h('dt', null, label), h('dd', null, value === null || value === undefined || value === false ? '—' : value));
}

export function chip(text: string, cls = ''): HTMLSpanElement {
  return h('span', { class: `chip ${cls}`.trim() }, text);
}

/** <time> with the absolute UTC time as a tooltip. */
export function timeEl(value: string | null | undefined, style: 'relative' | 'absolute' = 'relative'): HTMLElement {
  const d = parseDate(value);
  if (!d) return h('span', { class: 'muted' }, '—');
  return h(
    'time',
    { attrs: { datetime: d.toISOString(), title: fmtUtc(value) } },
    style === 'relative' ? fmtRelative(value) : fmtDateTime(value),
  );
}

// ---------------------------------------------------------------------------
// Loading / empty / error states

export function loadingState(text = 'Loading'): HTMLElement {
  return h('div', { class: 'state state--loading', attrs: { role: 'status' } }, h('span', null, text), h('span', { class: 'cursor', attrs: { 'aria-hidden': 'true' } }));
}

export function emptyState(title: string, detail?: string): HTMLElement {
  return h('div', { class: 'state state--empty' }, h('p', { class: 'state-title' }, title), detail ? h('p', null, detail) : null);
}

export function errorState(message: string, retry?: () => void, extra?: Node): HTMLElement {
  const box = h(
    'div',
    { class: 'state state--error', attrs: { role: 'alert' } },
    h('p', { class: 'state-title' }, 'Something went wrong'),
    h('p', null, message),
  );
  if (extra) box.append(extra);
  if (retry) box.append(h('button', { class: 'btn', attrs: { type: 'button' }, on: { click: () => retry() } }, 'Try again'));
  return box;
}

/**
 * A visually hidden page heading for pages whose visible <h1> only exists once data has
 * loaded (match replay, model profile): loading, error and not-found states keep one.
 */
export function pageHeading(text: string): HTMLElement {
  return h('h1', { class: 'sr-only' }, text);
}

export function notFoundState(title: string, detail: string): HTMLElement {
  return h(
    'div',
    { class: 'state state--error', attrs: { role: 'alert' } },
    h('p', { class: 'state-title' }, title),
    h('p', null, detail),
    h('p', null, link('/', 'Back to the leaderboard')),
  );
}

/** Replaces a container's content. */
export function show(container: Element, ...nodes: Child[]): void {
  container.replaceChildren();
  append(container, ...nodes);
}

// ---------------------------------------------------------------------------
// Misc

export function getParam(name: string): string | null {
  const v = new URLSearchParams(location.search).get(name);
  return v === null || v.trim() === '' ? null : v.trim();
}

/** Updates the page's query string without reloading or adding history entries. */
export function replaceQuery(params: Record<string, string | null | undefined>): void {
  const qs = new URLSearchParams(location.search);
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') qs.delete(k);
    else qs.set(k, v);
  }
  const query = qs.toString();
  history.replaceState(history.state, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Runs `fn` every `ms` while the page is visible, and once when it becomes visible again after a pause. */
export function everyWhileVisible(ms: number, fn: () => void): () => void {
  let last = Date.now();
  const tick = (): void => {
    if (document.hidden) return;
    last = Date.now();
    fn();
  };
  const timer = window.setInterval(tick, ms);
  const onVisible = (): void => {
    if (!document.hidden && Date.now() - last >= ms) tick();
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
